import { useState } from "react";
import { Loader2, Newspaper, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const { login, verify2fa, pending2fa, cancel2fa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const onLogin = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await verify2fa(code.trim());
      toast.success("Signed in");
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[var(--bg)] px-4 py-8">
      <div className="w-full max-w-md">
        {/* Masthead */}
        <div className="mb-8 flex flex-col items-center text-center">
          <div className="flex items-center gap-2 mb-2 text-[var(--ink-muted)]">
            <Newspaper className="w-4 h-4 text-[var(--accent)]" />
            <span className="overline">Newsdesk</span>
          </div>
          <div className="font-serif-display text-4xl leading-none tracking-tight">
            Newsdesk 1.0<span className="text-[var(--accent)]">.</span>
          </div>
          <div className="text-xs text-[var(--ink-muted)] mt-2 font-mono-display">
            editorial desk · sign in
          </div>
        </div>

        <div className="bg-white border border-[var(--border)] p-6 sm:p-8">
          {pending2fa ? (
            <form onSubmit={onVerify} className="space-y-5" data-testid="totp-form">
              <div className="flex items-start gap-3 text-sm text-[var(--ink)]">
                <ShieldCheck className="w-5 h-5 text-[var(--accent)] mt-0.5" />
                <div>
                  <div className="font-serif-display text-base">Two-factor code</div>
                  <div className="text-[var(--ink-muted)] text-xs mt-1">
                    Enter the 6-digit code from your authenticator app for{" "}
                    <span className="font-mono">{pending2fa.email}</span>.
                  </div>
                </div>
              </div>

              <div>
                <label className="overline text-[var(--ink-muted)] block mb-2">
                  Authenticator code
                </label>
                <input
                  data-testid="totp-code-input"
                  autoFocus
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) =>
                    setCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  placeholder="123456"
                  className="w-full border border-[var(--border)] px-3 py-2 text-center tracking-[0.5em] font-mono text-xl focus:outline-none focus:border-[var(--accent)]"
                />
              </div>

              {error && (
                <div className="text-sm text-red-600" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                data-testid="totp-verify-btn"
                type="submit"
                disabled={busy || code.length !== 6}
                className="w-full bg-[var(--slate-900)] text-white py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Verify & sign in
              </button>

              <button
                type="button"
                onClick={() => {
                  cancel2fa();
                  setCode("");
                  setError("");
                }}
                className="w-full text-xs text-[var(--ink-muted)] hover:text-[var(--ink)]"
              >
                Use a different account
              </button>
            </form>
          ) : (
            <form onSubmit={onLogin} className="space-y-5" data-testid="login-form">
              <div>
                <label className="overline text-[var(--ink-muted)] block mb-2">
                  Email
                </label>
                <input
                  data-testid="login-email-input"
                  autoFocus
                  type="email"
                  autoComplete="username"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label className="overline text-[var(--ink-muted)] block mb-2">
                  Password
                </label>
                <input
                  data-testid="login-password-input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-[var(--border)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--accent)]"
                  required
                />
              </div>

              {error && (
                <div className="text-sm text-red-600" data-testid="login-error">
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit-btn"
                type="submit"
                disabled={busy}
                className="w-full bg-[var(--slate-900)] text-white py-2.5 text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Sign in
              </button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center text-[10px] text-[var(--ink-muted)] font-mono-display">
          newsdesk · editorial platform
        </div>
      </div>
    </div>
  );
}
